import { type RefObject, useEffect, useRef, useState } from "react";

type TextRange = { start: number; end: number };
type AudioRange = { startSec: number; endSec: number };

// Merge overlapping marks for display only; draft offsets stay unchanged.
function highlightedText(text: string, marks: TextRange[]) {
	const ranges = marks
		.map(({ start, end }) => ({
			start: Math.max(0, Math.min(text.length, start)),
			end: Math.max(0, Math.min(text.length, end)),
		}))
		.filter(({ start, end }) => end > start)
		.sort((a, b) => a.start - b.start);
	const merged: TextRange[] = [];
	for (const range of ranges) {
		const previous = merged.at(-1);
		if (previous && range.start <= previous.end)
			previous.end = Math.max(previous.end, range.end);
		else merged.push({ ...range });
	}
	let offset = 0;
	const parts = merged.flatMap(({ start, end }) => {
		const before = text.slice(offset, start);
		offset = end;
		return [
			before,
			<mark key={`${start}-${end}`} className="bg-yellow-200 text-transparent">
				{text.slice(start, end)}
			</mark>,
		];
	});
	return [...parts, text.slice(offset), "\n"];
}

export function MarkedTranscript({
	text,
	marks,
	textareaRef,
}: {
	text: string;
	marks: TextRange[];
	textareaRef: RefObject<HTMLTextAreaElement | null>;
}) {
	const backdropRef = useRef<HTMLDivElement>(null);
	// biome-ignore lint/correctness/useExhaustiveDependencies: Text and mark changes alter rendered geometry and require scroll synchronization after the DOM update.
	useEffect(() => {
		const area = textareaRef.current;
		const backdrop = backdropRef.current;
		if (!area || !backdrop) return;
		const sync = () => {
			backdrop.style.width = `${area.clientWidth}px`;
			backdrop.scrollTop = area.scrollTop;
			backdrop.scrollLeft = area.scrollLeft;
		};
		sync();
		const observer = new ResizeObserver(sync);
		observer.observe(area);
		area.addEventListener("scroll", sync);
		return () => {
			observer.disconnect();
			area.removeEventListener("scroll", sync);
		};
	}, [textareaRef, text, marks]);
	return (
		<div className="relative mt-3 h-72 min-h-72 w-full resize-y overflow-hidden rounded-md border bg-slate-50 focus-within:outline-2 focus-within:outline-teal-700">
			<div
				ref={backdropRef}
				aria-hidden="true"
				className="pointer-events-none absolute inset-0 overflow-hidden whitespace-pre-wrap break-words p-3 font-sans text-sm leading-relaxed text-transparent"
			>
				{highlightedText(text, marks)}
			</div>
			<textarea
				ref={textareaRef}
				readOnly
				value={text}
				aria-label="Source transcript; select sensitive text"
				className="relative block h-full w-full resize-none border-0 bg-transparent p-3 font-sans text-sm leading-relaxed outline-none selection:bg-yellow-300 selection:text-yellow-950"
			/>
		</div>
	);
}

function timestamp(seconds: number) {
	const total = Math.max(0, Math.floor(seconds));
	return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, "0")}`;
}

export function MarkedRecording({
	url,
	peaks,
	duration,
	selection,
	marks,
	audioRef,
	waveformError = false,
	onPlaybackError,
	onDuration,
	onPickTime,
}: {
	url: string;
	peaks: number[];
	duration: number;
	selection: AudioRange;
	marks: AudioRange[];
	audioRef: RefObject<HTMLAudioElement | null>;
	waveformError?: boolean;
	onPlaybackError?: () => void;
	onDuration?: (duration: number) => void;
	onPickTime?: (seconds: number, extendSelection: boolean) => void;
}) {
	const [currentTime, setCurrentTime] = useState(0);
	// biome-ignore lint/correctness/useExhaustiveDependencies: A source URL change reloads the player and requires playback synchronization to restart.
	useEffect(() => {
		const audio = audioRef.current;
		if (!audio) return;
		let frame = 0;
		const sync = () =>
			setCurrentTime(
				Number.isFinite(audio.currentTime) ? audio.currentTime : 0,
			);
		const tick = () => {
			sync();
			frame = requestAnimationFrame(tick);
		};
		const play = () => {
			cancelAnimationFrame(frame);
			tick();
		};
		const stop = () => {
			cancelAnimationFrame(frame);
			sync();
		};
		const events = [
			"timeupdate",
			"seeking",
			"seeked",
			"loadedmetadata",
			"emptied",
		];
		for (const event of events) audio.addEventListener(event, sync);
		audio.addEventListener("play", play);
		audio.addEventListener("pause", stop);
		audio.addEventListener("ended", stop);
		sync();
		if (!audio.paused) play();
		return () => {
			cancelAnimationFrame(frame);
			for (const event of events) audio.removeEventListener(event, sync);
			audio.removeEventListener("play", play);
			audio.removeEventListener("pause", stop);
			audio.removeEventListener("ended", stop);
		};
	}, [audioRef, url]);
	const percent = (seconds: number) =>
		duration > 0 ? Math.max(0, Math.min(100, (seconds / duration) * 100)) : 0;
	const range = ({ startSec, endSec }: AudioRange) => ({
		left: `${percent(startSec)}%`,
		width: `${Math.max(0, percent(endSec) - percent(startSec))}%`,
	});
	const valid = ({ startSec, endSec }: AudioRange) =>
		duration > 0 && endSec > startSec && endSec > 0 && startSec < duration;
	const position = percent(currentTime);
	const pickTime = (seconds: number, extendSelection: boolean) => {
		if (!Number.isFinite(duration) || duration <= 0 || !audioRef.current)
			return;
		const time =
			Math.round(Math.max(0, Math.min(duration, seconds)) * 100) / 100;
		audioRef.current.currentTime = Math.min(duration, time);
		setCurrentTime(Math.min(duration, time));
		onPickTime?.(Math.min(duration, time), extendSelection);
	};
	return (
		<>
			<div
				aria-label="Audio waveform"
				role="slider"
				tabIndex={duration > 0 ? 0 : -1}
				aria-disabled={duration <= 0}
				aria-valuemin={0}
				aria-valuemax={duration}
				aria-valuenow={currentTime}
				aria-valuetext={`${timestamp(currentTime)} of ${timestamp(duration)}`}
				className="relative mt-4 cursor-crosshair rounded-md bg-slate-50 pt-7 focus-visible:outline-2 focus-visible:outline-teal-700"
				dir="ltr"
				onClick={(event) => {
					const bounds = event.currentTarget.getBoundingClientRect();
					if (bounds.width <= 0 || duration <= 0) return;
					event.currentTarget.focus();
					pickTime(
						((event.clientX - bounds.left) / bounds.width) * duration,
						event.shiftKey,
					);
				}}
				onKeyDown={(event) => {
					const target = (
						{
							ArrowLeft: currentTime - 1,
							ArrowDown: currentTime - 1,
							ArrowRight: currentTime + 1,
							ArrowUp: currentTime + 1,
							Home: 0,
							End: duration,
						} as Record<string, number>
					)[event.key];
					if (target === undefined) return;
					event.preventDefault();
					pickTime(target, event.shiftKey);
				}}
			>
				<div className="relative h-20 overflow-hidden rounded-b-md">
					{marks.filter(valid).map((mark, index) => (
						<div
							key={index}
							aria-label={`Audio mark ${mark.startSec}–${mark.endSec} seconds`}
							className="pointer-events-none absolute inset-y-0 border-x-2 border-yellow-600 bg-yellow-200"
							style={range(mark)}
						/>
					))}
					{valid(selection) && (
						<div
							aria-label="Selected audio span"
							className="pointer-events-none absolute inset-y-0 border-2 border-yellow-500 bg-yellow-300/60"
							style={range(selection)}
						/>
					)}
					<div
						aria-hidden="true"
						className="pointer-events-none absolute inset-0 flex items-center gap-px"
					>
						{peaks.map((peak, index) => (
							<span
								key={index}
								className="min-w-0 flex-1 rounded-sm bg-teal-700"
								style={{ height: `${Math.max(8, peak * 100)}%` }}
							/>
						))}
					</div>
					{!peaks.length && (
						<span className="relative text-xs text-muted-foreground">
							{waveformError
								? "Could not load the waveform. Use the player below to check the recording."
								: "Loading waveform…"}
						</span>
					)}
				</div>
				<div
					aria-label="Waveform playback position"
					className="pointer-events-none absolute bottom-0 top-6 w-0.5 bg-slate-900"
					style={{ left: `calc(${position}% - ${position === 100 ? 2 : 0}px)` }}
				/>
				<output
					aria-label="Playback timestamp"
					className="pointer-events-none absolute top-0 rounded bg-slate-900 px-1 py-0.5 text-xs tabular-nums text-white"
					style={{
						left: `clamp(0px, calc(${position}% - 1.5rem), calc(100% - 3rem))`,
					}}
				>
					{timestamp(currentTime)}
				</output>
			</div>
			<audio
				ref={audioRef}
				controls
				src={url}
				onError={onPlaybackError}
				onLoadedMetadata={(event) => {
					const value = event.currentTarget.duration;
					if (Number.isFinite(value) && value > 0) onDuration?.(value);
				}}
				className="mt-4 w-full"
			>
				<track kind="captions" />
			</audio>
		</>
	);
}
